import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import {
  Truck, Search, ArrowLeft, Wrench, AlertTriangle,
  ClipboardCheck, Plus, Upload, Camera, X, Check, Loader2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import AuthImage from '@/components/wom/AuthImage';

const NAVY = '#0E2A47';
const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const fmt = (d) => {
  if (!d) return null;
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1].slice(-2)}`;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
};
const STATUS_COLORS = {
  Good: 'bg-green-100 text-green-700 border-green-300',
  Monitor: 'bg-amber-100 text-amber-700 border-amber-300',
  'Needs Service': 'bg-orange-100 text-orange-700 border-orange-300',
  'Unsafe to Operate': 'bg-red-100 text-red-700 border-red-300',
};

const OperationsVehicles = () => {
  const { token, user } = useContext(AuthContext);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('');
  const [selId, setSelId] = useState(null);
  const [meta, setMeta] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/vehicle-ops/vehicles`, auth(token))
      .then((r) => setVehicles(r.data || [])).catch(() => setVehicles([])).finally(() => setLoading(false));
  }, [token]);
  useEffect(load, [load]);
  useEffect(() => { axios.get(`${API}/vehicle-ops/meta`, auth(token)).then((r) => setMeta(r.data)).catch(() => {}); }, [token]);

  const filtered = vehicles.filter((v) => {
    if (statusF && (v.status || 'active') !== statusF) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return `${v.veh_number} ${v.make} ${v.model} ${v.vin || ''} ${v.assigned_driver_name || ''}`.toLowerCase().includes(q);
  });

  if (selId) {
    return <VehicleOpsProfile vehicleId={selId} token={token} user={user} meta={meta} onBack={() => { setSelId(null); load(); }} />;
  }

  return (
    <div data-testid="ops-vehicles">
      <div className="mb-5">
        <h2 className="text-2xl font-bold" style={{ color: NAVY }}>Vehicles</h2>
        <p className="text-sm text-slate-500">Operations &amp; lifecycle — linked to the Admin Hub vehicle master. Identity &amp; assignment are read-only here.</p>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vehicle #, make/model, driver, VIN" data-testid="ops-veh-search" className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="ops-veh-status-filter" className="rounded-lg border border-slate-300 px-2 py-2 text-sm">
          <option value="">All statuses</option><option value="active">Active</option><option value="retired">Retired</option>
        </select>
      </div>

      {loading ? <div className="py-12 text-center text-slate-400">Loading…</div> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((v) => {
            const o = v.ops || {};
            return (
              <button key={v.id} onClick={() => setSelId(v.id)} data-testid={`ops-veh-card-${v.id}`} className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold" style={{ color: NAVY }}>#{v.veh_number}</span>
                  <div className="flex gap-1">
                    {o.open_unsafe > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">UNSAFE</span>}
                    {o.open_unsafe === 0 && o.open_findings > 0 && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">{o.open_findings} finding(s)</span>}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${(v.status || 'active') === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>{(v.status || 'active') === 'active' ? 'Active' : 'Retired'}</span>
                  </div>
                </div>
                <div className="mt-0.5 text-sm font-semibold text-slate-700">{v.year} {v.make} {v.model}</div>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                  <div><span className="text-slate-400">Driver:</span> {v.assigned_driver_name || 'Pool'}</div>
                  <div><span className="text-slate-400">Tag:</span> {v.tag || '—'}</div>
                  <div><span className="text-slate-400">Latest odo:</span> {o.current_odometer != null ? `${o.current_odometer}` : 'Unknown'}</div>
                  <div><span className="text-slate-400">Baseline:</span> {o.baseline_odometer != null ? `${o.baseline_odometer}` : '—'}</div>
                  <div><span className="text-slate-400">Last insp:</span> {fmt(o.last_inspection_date) || '—'}</div>
                  <div><span className="text-slate-400">This month:</span> <span className={o.current_month_status === 'submitted' ? 'text-green-600 font-semibold' : 'text-amber-600'}>{o.current_month_status === 'submitted' ? 'Done' : o.current_month_status === 'draft' ? 'Draft' : 'Due'}</span></div>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="col-span-full py-12 text-center text-slate-400">No vehicles match.</div>}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-sm font-medium text-slate-700">{children}</div></div>
);

const VehicleOpsProfile = ({ vehicleId, token, user, meta, onBack }) => {
  const [v, setV] = useState(null);
  const [showInsp, setShowInsp] = useState(false);
  const [showEvent, setShowEvent] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/vehicle-ops/vehicles/${vehicleId}`, auth(token)).then((r) => setV(r.data)).catch(() => {});
  }, [vehicleId, token]);
  useEffect(load, [load]);

  if (!v) return <div className="py-12 text-center text-slate-400">Loading…</div>;
  const o = v.ops || {};
  const isDriver = v.assigned_driver_id === user?.id;
  const canEvent = ['owner', 'admin', 'manager', 'tfs_operations', 'mechanic'].includes(user?.role);

  return (
    <div data-testid="ops-veh-profile">
      <button onClick={onBack} data-testid="ops-veh-back" className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700"><ArrowLeft size={15} /> Back to vehicles</button>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><span className="text-2xl font-bold" style={{ color: NAVY }}>#{v.veh_number}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${(v.status || 'active') === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>{(v.status || 'active') === 'active' ? 'Active' : 'Retired'}</span>
            </div>
            <div className="text-slate-700">{v.year} {v.make} {v.model}{v.trim ? ` ${v.trim}` : ''}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(isDriver || canEvent) && <button onClick={() => setShowInsp(true)} data-testid="ops-start-monthly-btn" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}><ClipboardCheck size={15} /> Monthly Inspection</button>}
            {canEvent && <button onClick={() => setShowEvent(true)} data-testid="ops-add-event-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Wrench size={15} /> Add Lifecycle Event</button>}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-4">
          <Row label="VIN">{v.vin || <span className="text-slate-400">—</span>}</Row>
          <Row label="Tag / Exp">{v.tag || '—'}{v.registration_expiration ? ` · ${fmt(v.registration_expiration)}` : ''}</Row>
          <Row label="Assigned Driver">{v.assigned_driver_name || 'Pool'}</Row>
          <Row label="Assignment">{fmt(v.assignment_start_date) || '—'}</Row>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 sm:grid-cols-4" data-testid="ops-status-row">
          <Row label="Latest Odometer">{o.current_odometer != null ? <>{o.current_odometer} <span className="text-[10px] text-slate-400">({fmt(o.current_odometer_date) || '—'})</span></> : <span className="text-slate-400">Unknown</span>}</Row>
          <Row label="Baseline Odometer">{o.baseline_odometer != null ? <>{o.baseline_odometer} <span className="text-[10px] text-slate-400">({fmt(o.baseline_odometer_date) || '—'})</span></> : <span className="text-slate-400">—</span>}</Row>
          <Row label="Last Service">{fmt(o.last_service_date) || <span className="text-slate-400">Unknown</span>}</Row>
          <Row label="Next Maintenance Due"><span className="text-slate-400">Needs verification</span></Row>
        </div>
        {(o.open_unsafe > 0 || o.open_findings > 0) && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
            <AlertTriangle size={16} /> {o.open_unsafe > 0 ? `${o.open_unsafe} unsafe report(s) flagged for mechanic review.` : `${o.open_findings} open finding(s) reported by driver.`}
          </div>
        )}
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Chronological History</h3>
        {(v.history || []).length === 0 ? <div className="py-6 text-center text-sm text-slate-400">No inspections or events recorded yet.</div> : (
          <div className="space-y-2">
            {v.history.map((h) => (
              <div key={h.id} data-testid={`ops-history-${h.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                <div className="flex items-center gap-3">
                  {h.kind === 'monthly_inspection' ? <ClipboardCheck size={16} className="text-blue-500" /> : <Wrench size={16} className="text-slate-500" />}
                  <div>
                    <div className="font-semibold text-slate-700">{h.title}{h.unsafe && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">UNSAFE</span>}{!h.unsafe && h.flagged && <span className="ml-2 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">FINDING</span>}</div>
                    <div className="text-xs text-slate-400">{fmt(h.date) || '—'} · {h.by || '—'}{h.odometer != null ? ` · ${h.odometer} mi` : ''}{h.status && h.kind === 'monthly_inspection' ? ` · ${h.status}` : ''}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showInsp && <MonthlyInspectionModal vehicle={v} token={token} meta={meta} onClose={() => setShowInsp(false)} onSaved={() => { setShowInsp(false); load(); }} />}
      {showEvent && <EventModal vehicle={v} token={token} meta={meta} onClose={() => setShowEvent(false)} onSaved={() => { setShowEvent(false); load(); }} />}
    </div>
  );
};

const MonthlyInspectionModal = ({ vehicle, token, meta, onClose, onSaved }) => {
  const [lang, setLang] = useState('en');
  const [odometer, setOdometer] = useState('');
  const [items, setItems] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [inspId, setInspId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const t = meta?.i18n?.[lang] || {};
  const mItems = meta?.monthly_items || [];

  const setItem = (key, field, val) => setItems((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: val } }));

  const buildBody = (status) => ({
    odometer: odometer === '' ? null : parseFloat(odometer),
    language: lang, notes, status,
    items: mItems.map((it) => ({ key: it.key, status: (items[it.key]?.status) || 'Good', note: items[it.key]?.note || null })),
  });

  const ensureSaved = async () => {
    if (inspId) { await axios.put(`${API}/vehicle-ops/monthly-inspections/${inspId}`, buildBody('draft'), auth(token)); return inspId; }
    const r = await axios.post(`${API}/vehicle-ops/vehicles/${vehicle.id}/monthly-inspections`, buildBody('draft'), auth(token));
    setInspId(r.data.id); return r.data.id;
  };

  const save = async (status) => {
    if (status === 'submitted') {
      if (odometer === '') { toast.error(lang === 'es' ? 'Ingrese el odómetro' : 'Enter the odometer reading'); return; }
      const hasProblem = mItems.some((it) => (meta.problem_statuses || []).includes(items[it.key]?.status));
      const hasDamagePhoto = photos.some((p) => ['other'].includes(p.category) || p.item_key);
      const hasOdoPhoto = photos.some((p) => p.category === 'dashboard_odometer');
      if (!hasOdoPhoto) { toast.error(lang === 'es' ? 'Se requiere foto del odómetro' : 'An odometer photo is required'); return; }
      if (hasProblem && !hasDamagePhoto) { toast.error(lang === 'es' ? 'Agregue una foto de cerca del problema' : 'Add a close-up photo of the reported problem'); return; }
    }
    setSaving(true);
    try {
      let id = inspId;
      if (id) await axios.put(`${API}/vehicle-ops/monthly-inspections/${id}`, buildBody(status), auth(token));
      else { const r = await axios.post(`${API}/vehicle-ops/vehicles/${vehicle.id}/monthly-inspections`, buildBody(status), auth(token)); id = r.data.id; setInspId(id); }
      toast.success(status === 'submitted' ? (lang === 'es' ? 'Inspección enviada' : 'Inspection submitted') : (lang === 'es' ? 'Borrador guardado' : 'Draft saved'));
      if (status === 'submitted') onSaved();
    } catch (e) { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const uploadPhoto = async (files, category) => {
    if (!files || !files.length) return;
    const id = await ensureSaved();
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f); fd.append('category', category);
      if (odometer) fd.append('odometer', odometer);
      try {
        const r = await axios.post(`${API}/vehicle-ops/monthly-inspections/${id}/photos`, fd, { headers: { Authorization: `Bearer ${token}` } });
        setPhotos((p) => [...p, r.data]);
      } catch { toast.error('Photo upload failed'); }
    }
  };

  const PHOTO_SLOTS = [
    { cat: 'dashboard_odometer', en: 'Odometer', es: 'Odómetro', required: true },
    { cat: 'front', en: 'Front', es: 'Frente' },
    { cat: 'rear', en: 'Rear', es: 'Parte trasera' },
    { cat: 'driver_side', en: 'Driver side', es: 'Lado conductor' },
    { cat: 'passenger_side', en: 'Passenger side', es: 'Lado pasajero' },
    { cat: 'other', en: 'Damage close-up', es: 'Daño (cerca)' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3" data-testid="ops-monthly-modal">
      <div className="my-4 w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="sticky top-0 flex items-center justify-between rounded-t-2xl border-b bg-white px-5 py-3">
          <div><div className="font-bold" style={{ color: NAVY }}>{t.title || 'Monthly Vehicle Inspection'}</div>
            <div className="text-xs text-slate-500">#{vehicle.veh_number} {vehicle.make} {vehicle.model} · {vehicle.assigned_driver_name || 'Pool'}</div></div>
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-lg border border-slate-300 text-xs font-semibold">
              <button onClick={() => setLang('en')} data-testid="ops-lang-en" className={`px-2 py-1 ${lang === 'en' ? 'bg-[#0E2A47] text-white' : 'text-slate-500'}`}>EN</button>
              <button onClick={() => setLang('es')} data-testid="ops-lang-es" className={`px-2 py-1 ${lang === 'es' ? 'bg-[#0E2A47] text-white' : 'text-slate-500'}`}>ES</button>
            </div>
            <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="text-xs font-semibold text-slate-600">{t.odometer || 'Odometer reading'} *</label>
            <input value={odometer} onChange={(e) => setOdometer(e.target.value)} type="number" data-testid="ops-insp-odometer" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>

          {mItems.map((it) => (
            <div key={it.key} className="rounded-lg border border-slate-200 p-3" data-testid={`ops-insp-item-${it.key}`}>
              <div className="mb-2 text-sm font-medium text-slate-700">{it[lang] || it.en}</div>
              <div className="flex flex-wrap gap-1.5">
                {(meta.item_statuses || []).map((s) => (
                  <button key={s} onClick={() => setItem(it.key, 'status', s)} data-testid={`ops-insp-${it.key}-${s.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${items[it.key]?.status === s ? STATUS_COLORS[s] : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                    {t.statuses?.[s] || s}
                  </button>
                ))}
              </div>
              {(meta.problem_statuses || []).includes(items[it.key]?.status) && (
                <input value={items[it.key]?.note || ''} onChange={(e) => setItem(it.key, 'note', e.target.value)} placeholder={t.note || 'Note'} className="mt-2 w-full rounded border border-slate-200 px-2 py-1 text-xs" />
              )}
            </div>
          ))}

          <div>
            <div className="mb-2 text-sm font-semibold text-slate-600">{lang === 'es' ? 'Fotos' : 'Photos'}</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {PHOTO_SLOTS.map((slot) => {
                const got = photos.filter((p) => p.category === slot.cat);
                return (
                  <div key={slot.cat} className="rounded-lg border border-slate-200 p-2">
                    <div className="mb-1 text-[11px] font-semibold text-slate-600">{slot[lang] || slot.en}{slot.required ? ' *' : ''}</div>
                    {got.length > 0 && <div className="mb-1 flex flex-wrap gap-1">{got.map((g) => <AuthImage key={g.id} src={`${API}/vehicle-ops/monthly-inspections/${inspId}/photos/${g.id}/image`} token={token} className="h-10 w-10 rounded object-cover" />)}</div>}
                    <div className="flex gap-1">
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-200 px-1.5 py-1 text-[10px] font-semibold text-slate-600" data-testid={`ops-photo-choose-${slot.cat}`}><Upload size={11} />{lang === 'es' ? 'Subir' : 'Upload'}<input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadPhoto(e.target.files, slot.cat); e.target.value = ''; }} /></label>
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-200 px-1.5 py-1 text-[10px] font-semibold text-slate-600" data-testid={`ops-photo-camera-${slot.cat}`}><Camera size={11} />{lang === 'es' ? 'Foto' : 'Take'}<input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { uploadPhoto(e.target.files, slot.cat); e.target.value = ''; }} /></label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={lang === 'es' ? 'Notas adicionales' : 'Additional notes'} rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 rounded-b-2xl border-t bg-white px-5 py-3">
          <button onClick={() => save('draft')} disabled={saving} data-testid="ops-insp-draft" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-50">{t.draft || 'Save draft'}</button>
          <button onClick={() => save('submitted')} disabled={saving} data-testid="ops-insp-submit" className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: NAVY }}>{saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}{t.submit || 'Submit'}</button>
        </div>
      </div>
    </div>
  );
};

const EventModal = ({ vehicle, token, meta, onClose, onSaved }) => {
  const [f, setF] = useState({ event_type: 'scheduled_maintenance', service_date: new Date().toISOString().slice(0, 10), odometer: '', performed_by: '', description: '', findings: '', follow_up: '', cost: '' });
  const [parts, setParts] = useState([]);
  const [warranty, setWarranty] = useState({ provider: '', term: '', start_date: '', expiration_date: '' });
  const [nextDue, setNextDue] = useState({ date: '', mileage: '', engine_hours: '' });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const set = (k, val) => setF((p) => ({ ...p, [k]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...f, odometer: f.odometer === '' ? null : parseFloat(f.odometer), cost: f.cost === '' ? null : parseFloat(f.cost),
        parts: parts.filter((p) => p.name).map((p) => ({ name: p.name, quantity: p.quantity ? parseFloat(p.quantity) : null, cost: p.cost ? parseFloat(p.cost) : null })),
        warranty: Object.values(warranty).some((x) => x) ? warranty : null,
        next_due: Object.values(nextDue).some((x) => x) ? { date: nextDue.date || null, mileage: nextDue.mileage ? parseFloat(nextDue.mileage) : null, engine_hours: nextDue.engine_hours ? parseFloat(nextDue.engine_hours) : null } : null,
      };
      const r = await axios.post(`${API}/vehicle-ops/vehicles/${vehicle.id}/events`, body, auth(token));
      setSavedId(r.data.id);
      toast.success('Event saved — you can attach receipts/photos now');
    } catch (e) { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const uploadAtt = async (files, kind) => {
    if (!savedId || !files?.length) return;
    for (const file of files) {
      const fd = new FormData(); fd.append('file', file); fd.append('kind', kind);
      try { await axios.post(`${API}/vehicle-ops/events/${savedId}/attachments`, fd, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Attached'); }
      catch { toast.error('Attach failed'); }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3" data-testid="ops-event-modal">
      <div className="my-4 w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div className="font-bold" style={{ color: NAVY }}>Add Lifecycle Event · #{vehicle.veh_number}</div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 p-5">
          <div><label className="text-xs font-semibold text-slate-600">Event type</label>
            <select value={f.event_type} onChange={(e) => set('event_type', e.target.value)} data-testid="ops-event-type" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm">
              {(meta?.event_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select></div>
          <div><label className="text-xs font-semibold text-slate-600">Service date</label><input type="date" value={f.service_date} onChange={(e) => set('service_date', e.target.value)} data-testid="ops-event-date" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Odometer</label><input type="number" value={f.odometer} onChange={(e) => set('odometer', e.target.value)} data-testid="ops-event-odometer" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Performed by / vendor</label><input value={f.performed_by} onChange={(e) => set('performed_by', e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div className="col-span-2"><label className="text-xs font-semibold text-slate-600">Description</label><textarea value={f.description} onChange={(e) => set('description', e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div className="col-span-2"><label className="text-xs font-semibold text-slate-600">Findings / recommended follow-up</label><textarea value={f.findings} onChange={(e) => set('findings', e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Total cost</label><input type="number" value={f.cost} onChange={(e) => set('cost', e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Next due (date)</label><input type="date" value={nextDue.date} onChange={(e) => setNextDue((p) => ({ ...p, date: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Next due (mileage)</label><input type="number" value={nextDue.mileage} onChange={(e) => setNextDue((p) => ({ ...p, mileage: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div>
          <div className="col-span-2 rounded-lg bg-slate-50 p-3">
            <div className="mb-1 text-xs font-bold text-slate-600">Warranty (optional)</div>
            <div className="grid grid-cols-2 gap-2">
              <input value={warranty.provider} onChange={(e) => setWarranty((p) => ({ ...p, provider: e.target.value }))} placeholder="Provider" className="rounded border border-slate-200 px-2 py-1 text-xs" />
              <input value={warranty.term} onChange={(e) => setWarranty((p) => ({ ...p, term: e.target.value }))} placeholder="Term" className="rounded border border-slate-200 px-2 py-1 text-xs" />
              <input type="date" value={warranty.start_date} onChange={(e) => setWarranty((p) => ({ ...p, start_date: e.target.value }))} className="rounded border border-slate-200 px-2 py-1 text-xs" />
              <input type="date" value={warranty.expiration_date} onChange={(e) => setWarranty((p) => ({ ...p, expiration_date: e.target.value }))} className="rounded border border-slate-200 px-2 py-1 text-xs" />
            </div>
          </div>
          <div className="col-span-2 rounded-lg bg-slate-50 p-3">
            <div className="mb-1 flex items-center justify-between"><span className="text-xs font-bold text-slate-600">Parts / materials</span><button onClick={() => setParts((p) => [...p, { name: '', quantity: '', cost: '' }])} className="text-xs font-semibold text-blue-600">+ Add part</button></div>
            {parts.map((p, i) => (
              <div key={i} className="mb-1 grid grid-cols-3 gap-2">
                <input value={p.name} onChange={(e) => setParts((a) => a.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Part" className="rounded border border-slate-200 px-2 py-1 text-xs" />
                <input value={p.quantity} onChange={(e) => setParts((a) => a.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} placeholder="Qty" type="number" className="rounded border border-slate-200 px-2 py-1 text-xs" />
                <input value={p.cost} onChange={(e) => setParts((a) => a.map((x, j) => j === i ? { ...x, cost: e.target.value } : x))} placeholder="Cost" type="number" className="rounded border border-slate-200 px-2 py-1 text-xs" />
              </div>
            ))}
          </div>
          {savedId && (
            <div className="col-span-2 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="mb-2 text-xs font-bold text-green-700">Event saved. Attach receipts / photos:</div>
              <div className="flex gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold" data-testid="ops-event-receipt"><FileText size={12} /> Receipt/Invoice<input type="file" className="hidden" onChange={(e) => { uploadAtt(e.target.files, 'receipt'); e.target.value = ''; }} /></label>
                <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold" data-testid="ops-event-photo"><Camera size={12} /> Photo<input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadAtt(e.target.files, 'photo'); e.target.value = ''; }} /></label>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t px-5 py-3">
          {savedId ? <button onClick={onSaved} className="rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>Done</button>
            : <button onClick={save} disabled={saving} data-testid="ops-event-save" className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: NAVY }}>{saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Save Event</button>}
        </div>
      </div>
    </div>
  );
};

export default OperationsVehicles;
